import { createBrowserClient } from "@supabase/ssr";
import { getSupabaseUrl, getSupabaseAnonKey } from "./config";
import { useAuthStore } from "@/stores/auth-store";
import {
  isDefinitiveInvalidRefreshTokenError,
  isRefreshRateLimitError,
  isTransientRefreshError,
  isBadRequestError,
} from "@/lib/auth/refresh-token-errors";
import { createAuthTokenFetchGuard } from "@/lib/auth/auth-token-fetch-guard";

declare global {
  interface Window {
    __growthsyncAuthListenerInitialized?: boolean;
  }
}

// Use single source of truth for Supabase configuration
// This ensures environment parity across localhost, production, and mobile
const supabaseUrl = getSupabaseUrl();
const supabaseAnonKey = getSupabaseAnonKey();

const AUTH_429_SUPPRESS_KEY = "growthsync-auth-429-suppress";
const AUTH_429_SUPPRESS_TTL_MS = 60000; // 1 min - avoid retrying immediately

function getAuthTokenKey(): string | null {
  const projectRef = supabaseUrl.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1];
  return projectRef ? `sb-${projectRef}-auth-token` : null;
}

/** Clear auth from localStorage - used to break 429/400 loop. Safe to call before client exists. */
function clearAuthFromStorageNow(): void {
  if (typeof window === "undefined") return;
  const key = getAuthTokenKey();
  if (key) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Non-fatal
    }
  }
}

/** Remove a dead mobile session from native persistence once, so it cannot be restored
 * into localStorage on the next launch. This deliberately bypasses GoTrue's storage
 * callbacks: a refresh storm can make every caller invoke those callbacks concurrently. */
function clearAuthFromNativePreferencesNow(): void {
  if (typeof window === "undefined" || typeof (window as any).Capacitor === "undefined") return;
  const key = getAuthTokenKey();
  if (!key) return;
  void import("@capacitor/preferences")
    .then(({ Preferences }) => Preferences.remove({ key }))
    .catch(() => {
      // localStorage has already been cleared. A future launch can retry native cleanup.
    });
}

/** Whether we're in auth backoff - no refresh/sync should run. Prevents 429/400 loop. */
let authBackoffActive = false;

/** Ref-count: multiple overlapping setSession/login paths must not drop the guard early.
 * (A boolean allowed a second guard to clear the flag while the first setSession was still in flight.) */
let setSessionGuardDepth = 0;
let resetInvalidTokenCircuit = () => {};

/** True while an explicit setSession (e.g. login) is in progress. Prevents fetch interceptor from
 * clearing storage when 400/429 occurs during setSession - which would abort the operation. */
function isSetSessionGuardActive(): boolean {
  return setSessionGuardDepth > 0;
}

/** Exposed for cookie-desync cleanup and diagnostics — do not call setSession based on this alone. */
export function isSetSessionInProgress(): boolean {
  return isSetSessionGuardActive();
}

/** Call this around setSession during login/signup. Prevents our fetch interceptor from clearing
 * storage on 400/429 during the operation (which would abort setSession and cause SIGNED_OUT spam). */
export function withSetSessionGuard<T>(fn: () => Promise<T>): Promise<T> {
  setSessionGuardDepth++;
  return fn().finally(() => {
    setSessionGuardDepth--;
  });
}

function isAuth429Suppressed(): boolean {
  if (typeof window === "undefined") return false;
  if (authBackoffActive) return true;
  try {
    const raw = sessionStorage.getItem(AUTH_429_SUPPRESS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Date.now() - ts > AUTH_429_SUPPRESS_TTL_MS) {
      sessionStorage.removeItem(AUTH_429_SUPPRESS_KEY);
      authBackoffActive = false;
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Check if auth backoff is active - callers should skip refresh/sync to prevent loops. */
export function isAuthBackoffActive(): boolean {
  return authBackoffActive || isAuth429Suppressed();
}

/**
 * Synchronous check: does localStorage have a refresh token?
 * Use this to avoid triggering Supabase refresh/getSession when there's nothing to restore.
 */
export function hasStoredRefreshToken(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const authTokenKey = getAuthTokenKey();
    if (!authTokenKey) return false;
    const raw = window.localStorage.getItem(authTokenKey);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!(parsed?.refresh_token);
  } catch {
    return false;
  }
}

function clearAuthBackoffState(): void {
  if (typeof window === "undefined") return;
  authBackoffActive = false;
  try {
    sessionStorage.removeItem(AUTH_429_SUPPRESS_KEY);
  } catch {}
}

/** Clear only rate-limit state while preserving a terminal invalid-token circuit. */
export function clearAuthBackoffAfterSessionRemoval(): void {
  clearAuthBackoffState();
}

/** Clear auth backoff and the invalid-token circuit before an explicit new sign-in. */
export function clearAuthBackoff(): void {
  clearAuthBackoffState();
  resetInvalidTokenCircuit();
}

function setAuth429Suppress(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(AUTH_429_SUPPRESS_KEY, Date.now().toString());
  } catch {}
}

/**
 * Pause token refreshes for a short window WITHOUT touching the stored session.
 *
 * This is the correct response to a 429. The session is still valid; we are simply being told to
 * slow down. Only isDefinitiveInvalidRefreshTokenError / a 400 means the token is actually dead, and
 * only that path should clear storage.
 */
function enterAuthBackoff(): void {
  if (typeof window === "undefined") return;
  if (authBackoffActive) return;
  authBackoffActive = true;
  setAuth429Suppress();
  console.warn("[Supabase Client] Rate limited on token refresh. Pausing refreshes; session kept.");
}

// CRITICAL: Intercept fetch to BREAK the 429/400 auth loop.
// 1. BLOCK token requests when backoff is active - prevents Supabase from ever hitting the server
// 2. On 400 invalid token: clear session WITHOUT 429 suppress (so the next login can refresh immediately)
// 3. On 429 from server: clear session with rate-limit backoff (separate from invalid token)
if (typeof window !== "undefined") {
  const _fetch = window.fetch;
  const tokenFetchGuard = createAuthTokenFetchGuard({
    fetch: _fetch.bind(window),
    isTokenRequest: (url) => url.startsWith(`${supabaseUrl}/auth/v1/token`),
    isSuppressed: isAuth429Suppressed,
    onInvalidToken: () => {
      if (!isSetSessionGuardActive()) {
        clearAuthFromNativePreferencesNow();
        clearAuthSessionFromStorage("invalid_session");
      }
    },
    onRateLimited: () => {
      if (!isSetSessionGuardActive()) enterAuthBackoff();
    },
  });

  resetInvalidTokenCircuit = tokenFetchGuard.resetInvalidTokenCircuit;
  window.fetch = tokenFetchGuard.fetch;
}

// Check if we're in Capacitor
const isCapacitor = typeof window !== "undefined" && (
  typeof (window as any).Capacitor !== "undefined" || 
  (window as any).Capacitor?.isNativePlatform?.() === true
);

// CRITICAL: Load tokens from Capacitor Preferences BEFORE Supabase client is initialized
// This ensures tokens are available when Supabase tries to read them on boot
// Use a promise that can be awaited to ensure session restoration completes
// CRITICAL: Wrap in timeout - on iOS/Android, Preferences can hang indefinitely in edge cases
const CAPACITOR_RESTORE_TIMEOUT_MS = 2500;
const CAPACITOR_IMPORT_TIMEOUT_MS = 2000;
export const capacitorSessionRestorePromise = (() => {
  if (typeof window === "undefined" || !isCapacitor) {
    return Promise.resolve();
  }
  
  return (async () => {
    try {
      const restoreTask = (async () => {
      // CRITICAL: Timeout the Preferences import - on Android, bridge may not be ready
      const { Preferences } = await Promise.race([
        import("@capacitor/preferences"),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("Capacitor Preferences import timeout")), CAPACITOR_IMPORT_TIMEOUT_MS)
        ),
      ]);
      
      // Extract project ref from Supabase URL to construct the auth token key
      // Supabase uses key format: "sb-{project-ref}-auth-token"
      const projectRef = supabaseUrl.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1];
      const authTokenKey = projectRef ? `sb-${projectRef}-auth-token` : null;
      
      let loadedCount = 0;
      
      // FAST PATH: On fresh install (no token in Preferences), resolve immediately
      // Avoids full scan and potential hang on Android
      //
      // A SLOW READ AND AN ABSENT TOKEN MUST NOT LOOK THE SAME. This raced Preferences.get against
      // a timer that resolved `{ value: null }`, which made "the disk is being slow" identical to
      // "this user has never logged in". The next branch then returned early as a fresh install, so
      // localStorage stayed empty, hasStoredRefreshToken() came back false, sessionRestorePromise
      // called stopAutoRefresh() and gave up. The user is signed out on their own phone, and
      // because auto-refresh is stopped it cannot recover until the app is relaunched. Nothing
      // reaches the auth server, so there is no server-side trace of it either, which is exactly
      // what the audit log showed on 2026-07-29: zero token requests while the app sat signed out.
      //
      // 1.5s is easy to exceed on a cold launch on a weak connection, which is precisely when
      // someone opens the app. The timer now resolves a distinct sentinel, so we can tell the two
      // apart: genuinely absent means fresh install and we skip the scan, while a timeout keeps
      // awaiting the same native read instead of starting a duplicate request.
      const RESTORE_TIMED_OUT = Symbol("capacitor-preferences-timeout");
      let shouldRetryAuthTokenRead = false;
      if (authTokenKey) {
        try {
          /* Keep exactly one native bridge call in flight for this key. On a physical
             iPhone the first Preferences.get sometimes had not answered by 1.5s. The
             old timeout branch then fell through to the full scan below, which issued a
             second Preferences.get for the same key while the first was still pending.
             The page heartbeat stopped at 1509ms, exactly when that duplicate call was
             created. Awaiting the original promise preserves the slow-read behaviour
             without re-entering the native plugin. The outer 2.5s restore limit still
             guarantees that the rest of app startup is never held indefinitely. */
          const authTokenRead = Preferences.get({ key: authTokenKey });
          const outcome = await Promise.race([
            authTokenRead,
            new Promise<typeof RESTORE_TIMED_OUT>((resolve) =>
              setTimeout(() => resolve(RESTORE_TIMED_OUT), 1500)
            ),
          ]);
          const { value } = outcome === RESTORE_TIMED_OUT ? await authTokenRead : outcome;
          if (value) {
            window.localStorage.setItem(authTokenKey, value);
            loadedCount++;
          } else {
            // Genuinely no token in Preferences: fresh install, nothing to restore.
            return;
          }
        } catch {
          // The first call finished with an error, so a later retry is not concurrent.
          shouldRetryAuthTokenRead = true;
        }
      }
      
      // Also check for any other Supabase keys that might be in Preferences
      let extraKeys: string[] = [];
      try {
        extraKeys = Object.keys(window.localStorage).filter(k =>
          (k.includes("sb-") || k.includes("supabase")) && k !== authTokenKey
        );
      } catch {
        // localStorage access can fail on some Android WebViews
      }
      // Retry the auth key only after a completed error. A slow call remains the sole
      // in-flight read and is never duplicated.
      const possibleSupabaseKeys = [
        ...(shouldRetryAuthTokenRead && authTokenKey ? [authTokenKey] : []),
        ...extraKeys,
      ];
      
      for (const key of possibleSupabaseKeys) {
        try {
          const { value } = await Preferences.get({ key });
          if (value) {
            const currentValue = window.localStorage.getItem(key);
            if (currentValue !== value) {
              // Restore to localStorage if it differs or doesn't exist
              window.localStorage.setItem(key, value);
              if (key !== authTokenKey) {
                loadedCount++;
              }
            }
          }
        } catch {
          // Ignore individual key errors
        }
      }
      
      // loadedCount tracked internally; no-op if nothing restored
      })();
      await Promise.race([
        restoreTask,
        new Promise<void>((_, reject) =>
          setTimeout(() => reject(new Error("Capacitor Preferences restore timeout")), CAPACITOR_RESTORE_TIMEOUT_MS)
        ),
      ]).catch((err) => {
        console.warn("[Supabase Client] Capacitor Preferences restore timeout or error (non-fatal):", err);
      });
    } catch (error) {
      console.warn("[Supabase Client] Error loading tokens from Capacitor Preferences:", error);
    }
  })();
})();

// CRITICAL: Create storage adapter that works for both web and Capacitor
// Web: uses localStorage (persists across tab closes)
// Capacitor: uses localStorage with sync bridge to Preferences for persistence across app closes
const createStorageAdapter = () => {
  if (typeof window === "undefined") return undefined;
  
  if (isCapacitor) {
    const authTokenKeyCap = getAuthTokenKey();
    return {
      getItem: (key: string): string | null => {
        try {
          // Deliberately NOT hiding the session during rate-limit backoff.
          //
          // This used to return null for the auth token whenever backoff was active, to stop
          // Supabase retrying. It did stop the retries, but it also told every part of the app that
          // nobody was signed in: AuthGate sat on the loading screen and the user was bounced to
          // login while their session was alive on the server the whole time.
          //
          // The retry loop is already stopped at the fetch layer, which blocks token requests while
          // the window is open, so hiding the session here bought nothing and cost the session.
          if (authTokenKeyCap && key === authTokenKeyCap) {
            const raw = window.localStorage.getItem(key);
            if (raw) {
              try {
                const parsed = JSON.parse(raw);
                if (!parsed?.refresh_token) return null;
              } catch {
                return null;
              }
            }
          }
          return window.localStorage.getItem(key);
        } catch (error) {
          console.warn("[Supabase Storage] Error reading from localStorage:", error);
          return null;
        }
      },
      setItem: (key: string, value: string): void => {
        // Store in localStorage for sync access
        try {
          window.localStorage.setItem(key, value);
        } catch (error) {
          console.warn("[Supabase Storage] Error writing to localStorage:", error);
        }
        // CRITICAL: Sync to Preferences immediately for persistence across app closes
        // Use Promise.resolve().then() to make it async but ensure it runs
        Promise.resolve().then(async () => {
          try {
            const { Preferences } = await import("@capacitor/preferences");
            await Preferences.set({ key, value });
          } catch {
            // CRITICAL: If Preferences write fails, retry once after a short delay
            setTimeout(async () => {
              try {
                const { Preferences } = await import("@capacitor/preferences");
                await Preferences.set({ key, value });
              } catch (retryError) {
                console.error("[Supabase Storage] Failed to persist token after retry:", retryError);
              }
            }, 500);
          }
        });
      },
      removeItem: (key: string): void => {
        // Remove from localStorage
        try {
          window.localStorage.removeItem(key);
        } catch (error) {
          console.warn("[Supabase Storage] Error removing from localStorage:", error);
        }
        // Async remove from Preferences (non-blocking)
        (async () => {
          try {
            const { Preferences } = await import("@capacitor/preferences");
            await Preferences.remove({ key });
          } catch (error) {
            console.warn("[Supabase Storage] Error removing from Capacitor Preferences:", error);
          }
        })();
      },
    };
  }
  
  // Web: use localStorage (persists across tab closes)
  const authTokenKey = getAuthTokenKey();
  return {
    getItem: (key: string): string | null => {
      try {
        // See the Capacitor adapter above: the session is deliberately NOT hidden during rate-limit
        // backoff. Hiding it stopped the refresh loop but simultaneously signed the user out of the
        // UI, which is the worse failure. The fetch-layer block already prevents the loop.

        // When no valid refresh_token in stored value, return null so Supabase won't attempt refresh
        if (authTokenKey && key === authTokenKey) {
          const raw = window.localStorage.getItem(key);
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              if (!parsed?.refresh_token) return null;
            } catch {
              return null;
            }
          }
        }
        return window.localStorage.getItem(key);
      } catch (error) {
        console.warn("[Supabase Storage] Error reading from localStorage:", error);
        return null;
      }
    },
    setItem: (key: string, value: string): void => {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        console.warn("[Supabase Storage] Error writing to localStorage:", error);
      }
    },
    removeItem: (key: string): void => {
      try {
        window.localStorage.removeItem(key);
      } catch (error) {
        console.warn("[Supabase Storage] Error removing from localStorage:", error);
      }
    },
  };
};

// Use createBrowserClient from @supabase/ssr to ensure cookies are set properly
// This allows API routes to read the session from cookies
// CRITICAL: Explicitly configure storage to ensure session persists across tab closes and app restarts
// By default, createBrowserClient uses localStorage with key "sb-{project-ref}-auth-token"
// We need to ensure it's configured correctly for session persistence
export const supabase = createBrowserClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: typeof window !== "undefined" ? createStorageAdapter() : undefined,
    autoRefreshToken: true, // CRITICAL: Automatically refresh tokens before they expire
    persistSession: true, // CRITICAL: Persist session to localStorage
    detectSessionInUrl: false, // Don't detect sessions in URL (security)
    // Don't override storageKey - let Supabase use its default key format
    // This ensures compatibility with Supabase's internal session management
    // Default key format: "sb-{project-ref}-auth-token"
  },
});

type RefreshResponse = Awaited<ReturnType<typeof supabase.auth.refreshSession>>;

const TRANSIENT_RETRY_DELAYS_MS = [400, 1000];
let inFlightRefreshPromise: Promise<RefreshResponse> | null = null;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function runRefreshWithRetry(): Promise<RefreshResponse> {
  let transientAttempt = 0;

  const MAX_REFRESH_ATTEMPTS = 3;
  for (let attempt = 0; attempt < MAX_REFRESH_ATTEMPTS; attempt++) {
    const response = await supabase.auth.refreshSession();
    const error = response.error;

    if (!error) {
      return response;
    }

    if (isDefinitiveInvalidRefreshTokenError(error)) {
      clearAuthSessionFromStorage("invalid_session");
      return response;
    }

    // CRITICAL: 400 Bad Request is terminal - invalid token, bad request, etc. Do NOT retry.
    if (isBadRequestError(error)) {
      console.warn("[Auth Refresh] Bad request (400) - clearing session, no retry");
      clearAuthSessionFromStorage("invalid_session");
      return response;
    }

    // On 429, do NOT retry: retrying compounds the rate limit. Back off instead, and KEEP the
    // session. A rate limit says "slow down", not "your token is dead", so the refresh token is
    // still valid and will work once the window closes. Clearing it here was signing people out of
    // a perfectly good session and sending them round the login loop that caused the 429.
    if (isRefreshRateLimitError(error)) {
      console.warn("[Auth Refresh] Rate limited (429) - pausing refreshes, session kept");
      enterAuthBackoff();
      return response;
    }

    if (isTransientRefreshError(error) && transientAttempt < TRANSIENT_RETRY_DELAYS_MS.length) {
      const retryDelayMs = TRANSIENT_RETRY_DELAYS_MS[transientAttempt];
      transientAttempt += 1;
      console.warn("[Auth Refresh] Transient refresh failure, retrying", {
        attempt: transientAttempt,
        retryDelayMs,
      });
      await sleep(retryDelayMs);
      continue;
    }

    return response;
  }

  return supabase.auth.refreshSession();
}

/**
 * Check if we have a refresh token in storage before making a network request.
 * Calling refreshSession with no token triggers unnecessary auth API requests
 * and can cause 429 rate limiting in production.
 */
function hasRefreshTokenInStorage(): boolean {
  if (typeof window === "undefined") return false;
  if (isAuth429Suppressed()) return false;
  try {
    const authTokenKey = getAuthTokenKey();
    if (!authTokenKey) return false;
    const stored = window.localStorage.getItem(authTokenKey);
    if (!stored) return false;
    const parsed = JSON.parse(stored);
    return !!(parsed?.refresh_token);
  } catch {
    return false;
  }
}

type AuthSessionClearReason = "invalid_session" | "rate_limited";

/**
 * Clear the Supabase auth session from storage and in-memory state without making a network request.
 * - invalid_session: dead refresh token / 400 — clear storage and Zustand, do NOT set the 429
 *   suppress window (otherwise the next login hits "Token refresh suppressed" for ~1 min).
 * - rate_limited: 429 loop — set backoff + suppress to stop hammering the auth API.
 */
function clearAuthSessionFromStorage(reason: AuthSessionClearReason = "invalid_session"): void {
  if (typeof window === "undefined") return;
  // CRITICAL: Local signOut() aborts in-flight GoTrue token requests. If a stale refresh fails with
  // 400/429 while email login is calling setSession, clearing here surfaces as AbortError and
  // "Session setup was interrupted" — same as SIGNED_OUT during setSession (already skipped below).
  if (isSetSessionGuardActive()) return;

  if (reason === "invalid_session") {
    // Clear any prior rate-limit guard, but keep the invalid-token circuit open for the rest of
    // this document. An explicit sign-in calls clearAuthBackoff() and resets that circuit.
    clearAuthBackoffState();
  } else {
    if (authBackoffActive) return;
    authBackoffActive = true;
    setAuth429Suppress();
  }

  clearAuthFromStorageNow();

  // CRITICAL: Clear Zustand auth state immediately (SIGNED_OUT may be skipped when rate_limited).
  try {
    const authState = useAuthStore.getState();
    if (authState.isAuthenticated || authState.user) {
      useAuthStore.setState({ user: null, isAuthenticated: false, membershipTier: "free" });
    }
  } catch {
    // Non-fatal - storage clear is the critical part
  }
  try {
    // CRITICAL: Stop the refresh timer - this is what was causing the loop.
    supabase.auth.stopAutoRefresh();
    supabase.auth.signOut({ scope: "local" });
  } catch {
    // Ignore - storage clear is the critical part
  }
}

export async function refreshSessionWithLock(): Promise<RefreshResponse> {
  if (inFlightRefreshPromise) {
    return inFlightRefreshPromise;
  }

  // CRITICAL: Skip network request when there's no refresh token in storage.
  // Prevents 429 rate limiting from repeated auth API calls in production.
  if (!hasRefreshTokenInStorage()) {
    return { data: { user: null, session: null }, error: { message: "No refresh token", name: "AuthSessionMissing", status: 0 } as any };
  }

  inFlightRefreshPromise = runRefreshWithRetry().finally(() => {
    inFlightRefreshPromise = null;
  });

  return inFlightRefreshPromise;
}

// CRITICAL: Restore session on app boot
// Load persisted tokens first (especially for Capacitor), then initialize Supabase
// This ensures sessions persist across browser/app closes
// Export a promise that can be awaited to ensure session restoration completes
export const sessionRestorePromise = (typeof window !== "undefined" ? (async () => {
  try {
    // CRITICAL: Skip restoration when user intentionally logged out
    const { isIntentionalLogout, clearIntentionalLogoutFlag } = await import("@/lib/auth/logout-flag");
    if (isIntentionalLogout()) {
      clearIntentionalLogoutFlag();
      return;
    }

    // CRITICAL: On Capacitor, load Preferences first (tokens may be there, not in localStorage yet)
    // This is essential for mobile - without this, session won't be restored on app launch
    if (isCapacitor) {
      await capacitorSessionRestorePromise;
      // Give additional time for localStorage to be populated from Preferences
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // CRITICAL: Early exit when no refresh token - do NOT clear Zustand here.
    // Timing: sessionRestorePromise runs very early; on mobile/refresh, storage may not be ready yet.
    // Let syncSession/SessionSync handle desync (they run later, only clear when hasStoredRefreshToken is false).
    if (!hasStoredRefreshToken()) {
      try {
        supabase.auth.stopAutoRefresh();
      } catch {
        /* non-fatal */
      }
      return;
    }
    
    // CRITICAL: First, verify localStorage actually has the session
    // This helps diagnose if localStorage is being cleared
    const projectRef = supabaseUrl.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1];
    const authTokenKey = projectRef ? `sb-${projectRef}-auth-token` : null;
    
    // Now get session from Supabase (which reads from localStorage via our storage adapter)
    // CRITICAL: Use getSession() which reads from storage, not getUser() which validates with server
    // This ensures we can restore sessions even if network is temporarily unavailable
    const { data: { session }, error } = await supabase.auth.getSession();
    
    // CRITICAL: If session exists, always sync to auth store - even if expired
    // The refresh will happen automatically via autoRefreshToken
    // This ensures users stay logged in even if token is expired (refresh will handle it)
    if (session && session.refresh_token) {
      // Check if access token is expired or close to expiring
      const expiresAt = session.expires_at ? session.expires_at * 1000 : null;
      const now = Date.now();
      
      if (expiresAt && expiresAt < now) {
        // Token is expired - try to refresh proactively
        try {
          const { data: { session: refreshedSession }, error: refreshError } = 
            await refreshSessionWithLock();
          if (refreshedSession && !refreshError) {
            await useAuthStore.getState().syncSession().catch(() => {});
          } else if (refreshError) {
            if (isDefinitiveInvalidRefreshTokenError(refreshError)) {
              // Stale/rotated refresh token: clear Supabase + persisted client state (cookies, Zustand).
              await useAuthStore.getState().clearClientAuthSession().catch(() => {});
            } else {
              // Network or other non-fatal issue - try to sync whatever is in storage
              await useAuthStore.getState().syncSession().catch(() => {});
            }
          }
        } catch {
          // Network error or other issue - preserve session
          await useAuthStore.getState().syncSession().catch(() => {});
        }
      } else if (session?.access_token || !session?.access_token) {
        // Session valid or in refresh state - sync to auth store
        await useAuthStore.getState().syncSession().catch(() => {});
      }
    } else if (session?.access_token) {
      await useAuthStore.getState().syncSession().catch(() => {});
    }
    // else: no session - do NOT clear Zustand; syncSession/SessionSync handle desync.
  } catch (error) {
    // On error - do not clear Zustand; syncSession/SessionSync handle desync with proper timing.
    console.warn(`[Auth Boot] Error during boot sequence:`, error);
  }
})() : Promise.resolve());

// CRITICAL: Track if this is the initial auth state change (to avoid clobbering sessions during boot)
if (typeof window !== "undefined") {
  if (!window.__growthsyncAuthListenerInitialized) {
    window.__growthsyncAuthListenerInitialized = true;
    let isInitialAuthStateChange = true;
    let lastSyncFromAuthStateChange = 0;
    let lastSignedOutAt = 0;
    let signedOutLogCount = 0;
    const AUTH_STATE_CHANGE_SYNC_DEBOUNCE_MS = 3000;
    const SIGNED_OUT_THROTTLE_MS = 2000; // Prevent SIGNED_OUT storm on desktop

    // Listen for auth state changes with targeted logging
    supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN") {
        clearAuthBackoff(); // Allow new session to be read by storage adapter
        try {
          supabase.auth.startAutoRefresh(); // Resume auto-refresh for the new session
        } catch {
          // Non-fatal
        }
        // Sync to auth store (but don't overwrite if we already have a session during initial load)
        if (!isInitialAuthStateChange || !useAuthStore.getState().isAuthenticated) {
          const now = Date.now();
          if (now - lastSyncFromAuthStateChange > AUTH_STATE_CHANGE_SYNC_DEBOUNCE_MS) {
            lastSyncFromAuthStateChange = now;
            useAuthStore.getState().syncSession().catch(() => {});
          }
        }
      } else if (event === "SIGNED_OUT") {
        // Skip entirely when backoff active - we've already cleared, avoid any re-entry/cascade
        if (authBackoffActive) {
          signedOutLogCount++;
          return;
        }
        // CRITICAL: Skip when setSession is in progress (e.g. login). Clearing storage here would
        // abort the in-flight setSession and cause "Session setup was interrupted" / AbortError.
        if (isSetSessionGuardActive()) {
          signedOutLogCount++;
          return;
        }
        const now = Date.now();
        const timeSinceLastSignedOut = now - lastSignedOutAt;
        // Throttle: prevent SIGNED_OUT storm (Supabase can emit thousands when refresh loop fails)
        if (timeSinceLastSignedOut < SIGNED_OUT_THROTTLE_MS && lastSignedOutAt > 0) {
          signedOutLogCount++;
          return;
        }
        signedOutLogCount = 0;
        lastSignedOutAt = now;
        // Normal sign-out or server-side session end: clear without rate-limit suppress (not a 429 loop).
        clearAuthSessionFromStorage("invalid_session");
      } else if (event === "TOKEN_REFRESHED") {
        // Debounce: avoid sync cascade that can cause 429 in production
        const now = Date.now();
        if (now - lastSyncFromAuthStateChange > AUTH_STATE_CHANGE_SYNC_DEBOUNCE_MS) {
          lastSyncFromAuthStateChange = now;
          useAuthStore.getState().syncSession().catch(() => {});
        }
      }

      // Mark initial load as complete after first event
      if (isInitialAuthStateChange) {
        isInitialAuthStateChange = false;
      }
    });
  }
}
